import { useEffect, useState } from 'preact/hooks'
import { Loader2, Pause, Play } from 'lucide-preact'
import { webinarRoomApi } from '@/api/webinarRoom'
import type { WebinarRoomPollViewDto, WebinarRoomRecordingPlaybackDto, WebinarRoomRecordingViewDto } from '@/api/webinarRoom'
import { playedFraction, shownLengthSeconds } from '@/webinarRoom/player/positions'
import { RoomPlayer } from '@/webinarRoom/player/RoomPlayer'
import { usePlayer } from '@/webinarRoom/player/usePlayer'
import { useSeekSlider } from '@/webinarRoom/player/useSeekSlider'
import { SubtitleControls } from '@/webinarRoom/subtitles/SubtitleControls'
import { OnDemandSubtitles } from '@/webinarRoom/subtitles/SubtitleLines'
import { useSubtitlePreference } from '@/webinarRoom/subtitles/useSubtitlePreference'
import { formatRecordingClock } from '@/webinarRoom/time'
import { formatPollTime } from './roomPolls'
import '@/webinarRoom/room.css'

interface RoomRecordingPreviewProps {
    workspaceId: string
    webId: string
    recording: WebinarRoomRecordingViewDto
    polls: readonly WebinarRoomPollViewDto[]
    /** Opened as "Preview with subtitles": the recording's subtitles start on. */
    subtitlesOn: boolean
    onClose: () => void
}

type PreviewPlayback = { status: 'loading' } | { status: 'ready'; playback: WebinarRoomRecordingPlaybackDto } | { status: 'failed' }

const FAILURE_CLASS = 'absolute inset-x-0 top-0 bottom-[19.6%] grid place-items-center p-5 text-center text-sm text-[#eef6f6]/90'

/** The recording's playback, asked for as the preview opens: its token is signed for this editor, never baked in. */
function usePreviewPlayback(workspaceId: string, webId: string): PreviewPlayback {
    const [playback, setPlayback] = useState<PreviewPlayback>({ status: 'loading' })
    useEffect(() => {
        let current = true
        webinarRoomApi.getRecordingPreview(workspaceId, webId).then(
            response => {
                if (!current) return
                const { data, status } = response
                setPlayback(status >= 200 && status < 300 && data ? { status: 'ready', playback: data } : { status: 'failed' })
            },
            () => { if (current) setPlayback({ status: 'failed' }) },
        )
        return () => { current = false }
    }, [workspaceId, webId])
    return playback
}

/**
 * The recording's private preview, in the client's skin: Mux Player on demand, a seek bar with
 * the polls marked in yellow where each pops up, the time, CC for the subtitles when it has them, and Close preview
 * (Escape closes it too). CC here is the preview's own; only the style it shows them in is the browser's choice.
 */
export function RoomRecordingPreview({ workspaceId, webId, recording, polls, subtitlesOn, onClose }: RoomRecordingPreviewProps) {
    const source = usePreviewPlayback(workspaceId, webId)
    const player = usePlayer()
    const [showSubtitles, setShowSubtitles] = useState(subtitlesOn)
    const { style, setStyle } = useSubtitlePreference()
    const subtitles = source.status === 'ready' ? source.playback.Subtitles ?? null : null
    const media = player.media.value
    const lengthSeconds = shownLengthSeconds(recording.DurationSeconds, player.duration.value)
    const slider = useSeekSlider({
        label: 'Position in the preview',
        timeSeconds: player.time.value,
        durationSeconds: lengthSeconds,
        onSeek: seconds => player.seek(seconds),
    })
    const fraction = playedFraction(slider.shownSeconds, lengthSeconds)

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    return (
        <div>
            <div className="vskin rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-[oklch(0.235_0.014_215)]">
                <div className="relative aspect-video">
                    {source.status === 'ready' && (
                        <RoomPlayer
                            player={player}
                            source={{
                                kind: 'on-demand',
                                playbackId: source.playback.PlaybackId,
                                playbackToken: source.playback.Token,
                                thumbnailToken: source.playback.ThumbnailToken,
                                autoplay: false,
                                title: recording.FileName,
                            }}
                            failureClass={FAILURE_CLASS}
                        />
                    )}
                    {subtitles && showSubtitles && <OnDemandSubtitles player={player} cues={subtitles.Cues} style={style} />}
                    {source.status === 'loading' && (
                        <div className="absolute inset-0 grid place-items-center text-[#eef6f6]/80" role="status">
                            <Loader2 className="w-6 h-6 animate-spin" aria-label="Loading the preview" />
                        </div>
                    )}
                    {source.status === 'failed' && <p className={FAILURE_CLASS} role="alert">The preview didn’t load. Close it and try again.</p>}
                </div>
                <div className="grid grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-x-2.5 px-3 pt-1 pb-2 text-[#e6f0f0]">
                    <div
                        className="col-span-4 relative h-5 cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#eef6f6] aria-disabled:cursor-default"
                        {...slider.props}
                    >
                        <span className="absolute inset-x-0 top-2 h-1 rounded-full bg-white/20" />
                        <span className="absolute inset-x-0 top-2 h-1 rounded-full bg-[#5fbfc4] origin-left" style={{ transform: `scaleX(${fraction})` }} />
                        {polls.filter(poll => poll.OffsetSeconds < lengthSeconds).map(poll => (
                            <span
                                key={poll.Id}
                                title={`Poll at ${formatPollTime(poll.OffsetSeconds)}`}
                                className="absolute top-1.5 w-2 h-2 -ml-1 rounded-full bg-[#f3c75a] shadow-[0_0_0_2px_oklch(0.235_0.014_215)]"
                                style={{ left: `${(poll.OffsetSeconds / lengthSeconds) * 100}%` }}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        disabled={media.status !== 'ready'}
                        aria-label={media.paused ? 'Play' : 'Pause'}
                        onClick={() => player.togglePlay()}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#eef6f6] hover:bg-white/10 disabled:opacity-50"
                    >
                        {media.paused ? <Play className="w-4 h-4" fill="currentColor" stroke="none" /> : <Pause className="w-4 h-4" fill="currentColor" stroke="none" />}
                    </button>
                    <span className="text-sm tabular-nums text-[#dcebeb]">{formatPollTime(slider.shownSeconds)} / {formatRecordingClock(lengthSeconds)}</span>
                    <span className="flex justify-end">
                        {subtitles && (
                            <SubtitleControls
                                languageCode={subtitles.LanguageCode}
                                on={showSubtitles}
                                onToggle={() => setShowSubtitles(!showSubtitles)}
                                style={style}
                                onPickStyle={setStyle}
                                buttonClass="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#eef6f6] hover:bg-white/10"
                                iconSize={16}
                                menu="above"
                            />
                        )}
                    </span>
                    <button type="button" onClick={onClose} className="px-2.5 py-1 rounded-md text-sm font-medium text-[#eef6f6] hover:bg-white/10">Close preview</button>
                </div>
            </div>
            <p className="mt-2.5 text-sm text-slate-500 dark:text-slate-400">
                Your preview only, and you can skip around in it. In the room it plays at the same point for everyone. The yellow marks are polls.
            </p>
        </div>
    )
}
